import { useState } from 'react';
import { Form, Button, Table, Alert, Spinner } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import { useUpdateCustomerMutation } from '../../slices/customersApiSlice';

const CustomerEditImportScreen = () => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [updateCustomer, { isLoading: loadingUpdate }] = useUpdateCustomerMutation();

  const isValidObjectId = (id) => /^[a-f\d]{24}$/i.test(id);

  const validateRow = (row) => {
    if (!row._id || !isValidObjectId(row._id.trim())) return 'Invalid or missing ID';
    if (!row.name || !row.telephone || !row.company || !row.serialNumber || !row.address) return 'Missing required fields';
    return null;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const parsed = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      const enriched = parsed.map((row, index) => ({
        index,
        _id: row._id?.trim(),
        name: row.name,
        telephone: row.telephone,
        company: row.company,
        serialNumber: row.serialNumber,
        address: row.address,
        error: validateRow(row),
        status: null,
      }));

      setRows(enriched);
      toast.success(`Loaded ${enriched.length} rows from ${file.name}`);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleRowSubmit = async (row, index) => {
    if (row.error) {
      toast.warning(`Row ${index + 1} skipped: ${row.error}`);
      return;
    }

    try {
      await updateCustomer({
        customerId: row._id,
        name: row.name,
        telephone: row.telephone,
        company: row.company,
        serialNumber: row.serialNumber,
        address: row.address,
      }).unwrap();

      const updated = [...rows];
      updated[index].status = { type: 'Success', message: 'Updated' };
      setRows(updated);
      toast.success(`Row ${index + 1} updated`);
    } catch (err) {
      const updated = [...rows];
      updated[index].status = {
        type: 'Failed',
        message: err?.data?.message || err.message,
      };
      setRows(updated);
      toast.error(`Row ${index + 1} failed: ${updated[index].status.message}`);
    }
  };

  const handleSubmitAll = async () => {
    const updatedRows = [...rows];

    for (let i = 0; i < updatedRows.length; i++) {
      const row = updatedRows[i];
      if (row.error) {
        updatedRows[i].status = { type: 'Skipped', message: row.error };
        continue;
      }

      try {
        await updateCustomer({
          customerId: row._id,
          name: row.name,
          telephone: row.telephone,
          company: row.company,
          serialNumber: row.serialNumber,
          address: row.address,
        }).unwrap();

        updatedRows[i].status = { type: 'Success', message: 'Updated' };
      } catch (err) {
        updatedRows[i].status = {
          type: 'Failed',
          message: err?.data?.message || err.message,
        };
      }
    }

    setRows(updatedRows);
    toast.success('✅ All customer rows processed');
  };

  return (
    <div className="container mt-4">
      <h2>👥 Bulk Customer Update</h2>

      <Form.Group controlId="excelFile" className="mb-3">
        <Form.Label>Upload Excel File (.xlsx)</Form.Label>
        <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
      </Form.Group>

      {rows.length > 0 && (
        <>
          <h5>Preview & Edit: {fileName}</h5>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Telephone</th>
                <th>Company</th>
                <th>Serial Number</th>
                <th>Address</th>
                <th>Submit</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} style={{ background: row.error ? '#fff3cd' : 'inherit' }}>
                  <td><Form.Control value={row._id} readOnly plaintext /></td>
                  <td><Form.Control value={row.name} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].name = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.telephone} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].telephone = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.company} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].company = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.serialNumber} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].serialNumber = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td><Form.Control value={row.address} onChange={(e) => {
                    const updated = [...rows];
                    updated[idx].address = e.target.value;
                    setRows(updated);
                  }} /></td>
                  <td>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleRowSubmit(row, idx)}
                      disabled={loadingUpdate}
                    >
                      {loadingUpdate ? <Spinner animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </td>
                  <td>
                    {row.status ? (
                      <span
                        className={
                          row.status.type === 'Success'
                            ? 'text-success'
                            : row.status.type === 'Skipped'
                            ? 'text-warning'
                            : 'text-danger'
                        }
                      >
                        {row.status.type} — {row.status.message}
                      </span>
                    ) : row.error ? (
                      <span className="text-danger">{row.error}</span>
                    ) : (
                      <span className="text-muted">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Button
            variant="success"
            onClick={handleSubmitAll}
            disabled={loadingUpdate}
            className="mb-3"
          >
            {loadingUpdate ? <Spinner animation="border" size="sm" /> : '🚀 Submit All Valid Rows'}
          </Button>
        </>
      )}

      {rows.some((row) => row.status) && (
        <div className="mt-4">
          <h5>📋 Submission Summary</h5>
          {rows.map((row, i) =>
            row.status ? (
              <Alert
                key={i}
                variant={
                  row.status.type === 'Success'
                    ? 'success'
                    : row.status.type === 'Skipped'
                    ? 'warning'
                    : 'danger'
                }
              >
                Row {i + 1}: {row.status.type} — {row.status.message}
              </Alert>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerEditImportScreen;